import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { mcqAPI, responseAPI } from '../utils/api';
import {
  RiBookOpenLine, RiTimeLine, RiCheckboxCircleLine, RiArrowLeftLine,
  RiArrowRightLine, RiLoaderLine, RiErrorWarningLine,
} from 'react-icons/ri';

/* ── Main page ──────────────────────────────────────────────── */
const StudentMCQPage = () => {
  const { shareCode } = useParams();
  const [mcq, setMcq]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchMCQ = async () => {
      try {
        const response = await mcqAPI.getMCQByShareCode(shareCode);
        setMcq(response.data.mcq);
      } catch (err) {
        setError(err.response?.data?.message || 'MCQ not found');
      } finally {
        setLoading(false);
      }
    };
    fetchMCQ();
  }, [shareCode]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center animate-fade-in-up">
        <RiErrorWarningLine className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">Test Not Found</h1>
        <p className="text-slate-500 text-sm">{error}</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center animate-fade-in-up">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <RiCheckboxCircleLine className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Test Submitted!</h1>
        <p className="text-slate-500 text-sm">
          Your responses have been recorded. Your teacher will share the results with you.
        </p>
      </div>
    </div>
  );

  return mcq ? (
    <MCQForm mcq={mcq} shareCode={shareCode} onSubmit={() => setSubmitted(true)} />
  ) : null;
};

/* ── MCQ Form ───────────────────────────────────────────────── */
const MCQForm = ({ mcq, shareCode, onSubmit }) => {
  const [formData, setFormData] = useState({
    studentName: '',
    rollNumber:  '',
    className:   mcq.className || '',
    section:     '',
    responses:   {},
  });
  const [testStarted, setTestStarted]   = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft]         = useState(mcq.duration * 60);
  const [submitting, setSubmitting]     = useState(false);
  const [errors, setErrors]             = useState({});

  /* Timer */
  useEffect(() => {
    const handleAutoSubmit = async () => {
      if (Object.keys(formData.responses).length === 0) return;
      await handleSubmit(true);
    };
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { handleAutoSubmit(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: '' });
  };

  const handleAnswerChange = (questionNumber, answer) => {
    setFormData({ ...formData, responses: { ...formData.responses, [questionNumber]: answer } });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.studentName.trim()) newErrors.studentName = 'Student name is required';
    if (!formData.rollNumber.trim())  newErrors.rollNumber  = 'Roll number is required';
    if (!formData.className.trim())   newErrors.className   = 'Class is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (autoSubmit = false) => {
    if (!validateForm() && !autoSubmit) return;
    setSubmitting(true);
    try {
      const responses = mcq.questions.map(q => ({
        questionNumber: q.questionNumber,
        selectedAnswer: formData.responses[q.questionNumber] || '',
      }));
      await responseAPI.submitResponse({
        mcqId:       mcq._id,
        studentName: formData.studentName,
        rollNumber:  formData.rollNumber,
        className:   formData.className,
        section:     formData.section,
        responses,
        timeTaken:   (mcq.duration * 60) - timeLeft,
      });
      onSubmit();
    } catch (error) {
      console.error('Error submitting response:', error);
      alert('Error submitting response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const question   = mcq.questions[currentQuestion];
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  const answeredCount = Object.keys(formData.responses).length;

  /* ── Info / registration screen ── */
  if (!testStarted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-lg animate-fade-in-up">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Header */}
            <div className="bg-slate-800 px-6 py-5">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <RiBookOpenLine className="w-4 h-4 text-white" />
                </div>
                <span className="text-slate-400 text-xs font-medium uppercase tracking-wide">MCQ Test</span>
              </div>
              <h1 className="text-xl font-bold text-white">{mcq.title}</h1>
              {mcq.description && <p className="text-slate-400 text-sm mt-1">{mcq.description}</p>}
            </div>

            {/* Test info */}
            <div className="grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
              {[
                { icon: RiBookOpenLine, label: 'Questions', value: mcq.totalQuestions },
                { icon: RiTimeLine,     label: 'Duration',  value: `${mcq.duration} min` },
                { icon: RiCheckboxCircleLine, label: 'Pass Mark', value: `${mcq.passingPercentage}%` },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex flex-col items-center py-4 px-3 text-center">
                  <Icon className="w-4 h-4 text-blue-500 mb-1" />
                  <p className="text-base font-bold text-slate-800">{value}</p>
                  <p className="text-xs text-slate-400">{label}</p>
                </div>
              ))}
            </div>

            {/* Form */}
            <form
              onSubmit={e => { e.preventDefault(); if (validateForm()) setTestStarted(true); }}
              className="p-6 space-y-4"
            >
              <div>
                <label className="label">Student Name *</label>
                <input
                  className={`input ${errors.studentName ? 'border-red-400 focus:ring-red-400' : ''}`}
                  type="text" name="studentName" value={formData.studentName}
                  onChange={handleInputChange} placeholder="Enter your full name"
                />
                {errors.studentName && <p className="text-red-500 text-xs mt-1">{errors.studentName}</p>}
              </div>

              <div>
                <label className="label">Roll Number *</label>
                <input
                  className={`input ${errors.rollNumber ? 'border-red-400 focus:ring-red-400' : ''}`}
                  type="text" name="rollNumber" value={formData.rollNumber}
                  onChange={handleInputChange} placeholder="Enter your roll number"
                />
                {errors.rollNumber && <p className="text-red-500 text-xs mt-1">{errors.rollNumber}</p>}
              </div>

              <div>
                <label className="label">Class</label>
                <input
                  className="input bg-slate-50 text-slate-500 cursor-not-allowed"
                  type="text" name="className" value={formData.className} readOnly
                />
              </div>

              <div>
                <label className="label">Section <span className="text-slate-400 font-normal">(Optional)</span></label>
                <input
                  className="input" type="text" name="section" value={formData.section}
                  onChange={handleInputChange} placeholder="Enter your section"
                />
              </div>

              <button type="submit"
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 rounded-xl transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 active:scale-[0.98] mt-2">
                Start Test
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  /* ── Test screen ── */
  return (
    <div className="min-h-screen bg-slate-50 p-4 py-6">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header bar */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-bold text-slate-800">{mcq.title}</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Question {currentQuestion + 1} of {mcq.totalQuestions} · {answeredCount} answered
              </p>
            </div>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg flex-shrink-0 ${timeLeft < 60 ? 'bg-red-50 border border-red-200' : 'bg-slate-50 border border-slate-200'}`}>
              <RiTimeLine className={`w-4 h-4 ${timeLeft < 60 ? 'text-red-500' : 'text-slate-500'}`} />
              <span className={`font-mono text-lg font-bold ${timeLeft < 60 ? 'text-red-600' : 'text-slate-700'}`}>
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>
          {/* Progress */}
          <div className="mt-3">
            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuestion + 1) / mcq.totalQuestions) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Question card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <p className="font-semibold text-slate-800 mb-5 leading-relaxed">{question.questionText}</p>

          <div className="space-y-2.5">
            {question.options.map((option) => {
              const selected = formData.responses[question.questionNumber] === option.optionLetter;
              return (
                <label key={option.optionLetter}
                  className={`flex items-start gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all duration-150 ${
                    selected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}>
                  <input
                    type="radio"
                    name={`question-${question.questionNumber}`}
                    value={option.optionLetter}
                    checked={selected}
                    onChange={() => handleAnswerChange(question.questionNumber, option.optionLetter)}
                    className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0"
                  />
                  <span>
                    <span className={`font-bold mr-1 ${selected ? 'text-blue-600' : 'text-slate-500'}`}>
                      {option.optionLetter}.
                    </span>
                    <span className={selected ? 'text-slate-800' : 'text-slate-700'}>{option.optionText}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2.5">Question Navigator</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {mcq.questions.map((q, idx) => (
              <button key={idx} onClick={() => setCurrentQuestion(idx)}
                className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                  currentQuestion === idx
                    ? 'bg-blue-600 text-white shadow-sm'
                    : formData.responses[q.questionNumber]
                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}>
                {idx + 1}
              </button>
            ))}
          </div>

          <div className="flex gap-3">
            <button onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
              disabled={currentQuestion === 0}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-sm font-medium">
              <RiArrowLeftLine className="w-4 h-4" /> Previous
            </button>

            {currentQuestion < mcq.totalQuestions - 1 ? (
              <button onClick={() => setCurrentQuestion(currentQuestion + 1)}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all text-sm font-semibold active:scale-[0.98]">
                Next <RiArrowRightLine className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={() => handleSubmit()} disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-all text-sm font-semibold disabled:opacity-60 active:scale-[0.98]">
                {submitting ? <><RiLoaderLine className="w-4 h-4 animate-spin" /> Submitting…</> : 'Submit Test'}
              </button>
            )}
          </div>
        </div>

        {/* Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-xs text-amber-700 flex items-start gap-2">
          <RiErrorWarningLine className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p><span className="font-semibold">Reminder:</span> Review all answers before submitting. You cannot change answers after submission.</p>
        </div>
      </div>
    </div>
  );
};

export default StudentMCQPage;
